import { keyframes } from '@mui/material/styles';

import { controlTokens, loadingTokens, surfaceTokens } from '@/theme/themePrimitives';

export const signalSpin = keyframes`
  to { transform: rotate(360deg); }
`;

/** Brand loader B1: each ridge lights up from the core outwards, holds, then leaves. */
export const signalPrint = keyframes`
  0% { opacity: 0; transform: scale(0.9); }
  16% { opacity: 1; transform: scale(1); }
  72% { opacity: 1; transform: scale(1); }
  90%, 100% { opacity: 0; transform: scale(1.03); }
`;

/** Pause after each B1 print so a resting control does not read as a loader. */
export const BRAND_PRINT_INTERVAL_MS = 12_000;

/**
 * The same B1 print, then the drawn symbol holds. Start and end match, so the loop
 * does not blink. Built for `BRAND_PRINT_INTERVAL_MS` after `loadingTokens.brandCycle`.
 */
export const signalPrintInterval = (() => {
  const cycle = loadingTokens.brandCycle;
  const total = cycle + BRAND_PRINT_INTERVAL_MS;
  const restEnd = (BRAND_PRINT_INTERVAL_MS / total) * 100;
  const at = (printPercent: number) => restEnd + (printPercent / 100) * (100 - restEnd);

  return keyframes`
    0%, ${restEnd}% { opacity: 1; transform: scale(1); }
    ${at(2)}% { opacity: 0; transform: scale(0.9); }
    ${at(16)}% { opacity: 1; transform: scale(1); }
    ${at(72)}% { opacity: 1; transform: scale(1); }
    ${at(90)}% { opacity: 0; transform: scale(1.03); }
    100% { opacity: 1; transform: scale(1); }
  `;
})();

/** Brand loader B2: the whole symbol stays and a wave of light runs through the ridges. */
export const signalPulse = keyframes`
  0%, 100% { opacity: 0.22; }
  35% { opacity: 1; }
`;

/** Reduced motion: the symbol breathes, never scales. */
export const signalBreathe = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
`;

/** Indeterminate progress: a 32% segment runs across the track. */
export const signalIndeterminate = keyframes`
  0% { transform: translateX(-110%); }
  100% { transform: translateX(330%); }
`;

/** `double` is two Crista rings, `triple` three; from 40 px up the spinner shows all four. */
export type CristaSpinnerVariant = 'double' | 'triple';
export type BrandSymbolAnimation = 'print' | 'pulse' | 'none';

/** Ridge `index` starts after this delay, so the six light up in sequence. */
export function getBrandRidgeDelay(animation: BrandSymbolAnimation, index: number) {
  if (animation === 'pulse') return index * 120;
  return index * loadingTokens.brandStagger;
}

/**
 * The official symbol (brand-guardian `bodyprint-symbol-*-transparent.svg`), one path per
 * ridge, from the core outwards. The official gradient is one fixed tone per ridge, from
 * Coral at the core to Vivid Red at the top. `index.html` inlines the same paths for the
 * boot loader: change both together.
 */
export const BRAND_SYMBOL_VIEWBOX = '0 0 684 804';
export const BRAND_SYMBOL_ASPECT = 684 / 804;
/** The core the ridges grow from, in viewBox units. */
export const BRAND_SYMBOL_ORIGIN = '425px 700px';

export const BRAND_SYMBOL_RIDGES = [
  {
    d: 'M461 608.5Q453 608 441 613.5Q429 619 421.5 624.5Q414 630 407 637Q400 644 391 656.5Q382 669 377.5 678.5Q373 688 370.5 696.5Q368 705 367 720Q366 735 367.5 743Q369 751 371.5 757.5Q374 764 377 769.5Q380 775 386 782Q392 789 398 792Q404 795 408.5 795Q413 795 418 793Q423 791 427.5 784.5Q432 778 431.5 770.5Q431 763 425 753.5Q419 744 417.5 738Q416 732 416 725Q416 718 418.5 708.5Q421 699 426 690.5Q431 682 439.5 673.5Q448 665 461.5 657.5Q475 650 479 644.5Q483 639 483.5 633Q484 627 481 621Q478 615 473.5 612Q469 609 461 608.5Z',
    color: '#FF5351',
  },
  {
    d: 'M483 516.5Q465 515 447 518.5Q429 522 409 531.5Q389 541 372 554Q355 567 339.5 583.5Q324 600 312 617.5Q300 635 292 650.5Q284 666 280 676Q276 686 272 702.5Q268 719 267.5 733Q267 747 270 760.5Q273 774 279 780.5Q285 787 293 788.5Q301 790 308 787Q315 784 319 777.5Q323 771 322 759Q321 747 322 730Q323 713 327 699Q331 685 337.5 671.5Q344 658 357 639.5Q370 621 380.5 610Q391 599 399 592.5Q407 586 420 578.5Q433 571 446.5 567Q460 563 473 563Q486 563 491.5 564.5Q497 566 504 570Q511 574 515.5 578.5Q520 583 523.5 589Q527 595 529 604.5Q531 614 530.5 619.5Q530 625 527.5 632.5Q525 640 521.5 646Q518 652 511 659Q504 666 497 670Q490 674 485.5 678.5Q481 683 480 685.5Q479 688 479.5 695Q480 702 482.5 706Q485 710 488.5 712.5Q492 715 496.5 716Q501 717 505 716.5Q509 716 521.5 709Q534 702 543 694Q552 686 558 678Q564 670 568 662.5Q572 655 576 641Q580 627 579.5 611Q579 595 574 581Q569 567 562.5 558Q556 549 544 539.5Q532 530 516.5 524Q501 518 483 516.5Z',
    color: '#FF4C4F',
  },
  {
    d: 'M488.5 414.5Q470 413 457 414Q444 415 429 418Q414 421 398 426.5Q382 432 370.5 437.5Q359 443 338 456.5Q317 470 305.5 479.5Q294 489 279 504Q264 519 254 531Q244 543 233 558.5Q222 574 205 605Q188 636 179 660.5Q170 685 167 697.5Q164 710 162.5 720Q161 730 161 740Q161 750 163.5 755.5Q166 761 171.5 765Q177 769 185 769.5Q193 770 197.5 768Q202 766 205.5 762.5Q209 759 211 753.5Q213 748 214 734Q215 720 219 702.5Q223 685 229 669Q235 653 241.5 639.5Q248 626 261 605Q274 584 285.5 569.5Q297 555 306.5 545Q316 535 331.5 521.5Q347 508 355.5 502Q364 496 379.5 487.5Q395 479 405 475Q415 471 429 467.5Q443 464 450.5 463Q458 462 473 462Q488 462 497.5 463.5Q507 465 518.5 469Q530 473 541 478Q552 483 560 488.5Q568 494 577 502.5Q586 511 593.5 521Q601 531 606.5 542Q612 553 615.5 564Q619 575 620.5 586Q622 597 621.5 609Q621 621 617 637Q613 653 608 663.5Q603 674 595 685Q587 696 579.5 703.5Q572 711 555 722.5Q538 734 526.5 738.5Q515 743 503 745.5Q491 748 485.5 752Q480 756 478 759.5Q476 763 475.5 769Q475 775 478.5 781.5Q482 788 487.5 791Q493 794 500.5 794Q508 794 527 788.5Q546 783 556.5 778Q567 773 577.5 766.5Q588 760 597.5 752.5Q607 745 616 736Q625 727 634.5 714Q644 701 651.5 685.5Q659 670 662.5 659Q666 648 668 637Q670 626 670.5 608Q671 590 668 572.5Q665 555 659.5 540Q654 525 645 509.5Q636 494 624.5 481Q613 468 599 457Q585 446 566 436.5Q547 427 527 421.5Q507 416 488.5 414.5Z',
    color: '#FF434D',
  },
  {
    d: 'M404 214Q390 214 375 217Q360 220 346 224.5Q332 229 304.5 242Q277 255 251.5 271Q226 287 203 304.5Q180 322 160 340Q140 358 115.5 384Q91 410 69 438Q47 466 29 492Q11 518 9 522.5Q7 527 7 533Q7 539 8 542Q9 545 12.5 549Q16 553 20 555Q24 557 29 557.5Q34 558 38 557Q42 556 46.5 553Q51 550 67.5 524.5Q84 499 98.5 479.5Q113 460 127 443.5Q141 427 157.5 410Q174 393 205 366.5Q236 340 256.5 326Q277 312 292.5 303Q308 294 321.5 287.5Q335 281 349.5 275.5Q364 270 377 267Q390 264 401.5 263.5Q413 263 418.5 264.5Q424 266 429.5 270.5Q435 275 437.5 280.5Q440 286 439.5 292.5Q439 299 437.5 302Q436 305 432 309Q428 313 424 315Q420 317 405 319Q390 321 369 328Q348 335 329 345Q310 355 297.5 363.5Q285 372 263 388.5Q241 405 211 434.5Q181 464 161.5 488.5Q142 513 124 541Q106 569 90 601Q74 633 63.5 663Q53 693 54 701.5Q55 710 59 714.5Q63 719 70 721.5Q77 724 81.5 723.5Q86 723 89.5 721.5Q93 720 98 714.5Q103 709 106 697Q109 685 116 665.5Q123 646 140.5 611.5Q158 577 175.5 551.5Q193 526 210.5 505.5Q228 485 251 463Q274 441 296 424.5Q318 408 344.5 394Q371 380 384.5 375.5Q398 371 410.5 368.5Q423 366 434.5 361.5Q446 357 453.5 352Q461 347 470 337Q479 327 484 314.5Q489 302 489.5 291.5Q490 281 488 272.5Q486 264 480 253.5Q474 243 467 236.5Q460 230 449.5 224.5Q439 219 428.5 216.5Q418 214 404 214Z',
    color: '#FF2848',
  },
  {
    d: 'M413.5 108Q401 108 384 110Q367 112 349.5 116Q332 120 314 126Q296 132 275.5 141Q255 150 236.5 160Q218 170 194.5 185Q171 200 156.5 210.5Q142 221 118 241Q94 261 69.5 284.5Q45 308 42.5 313.5Q40 319 40.5 326Q41 333 44.5 338Q48 343 55.5 346Q63 349 69 348Q75 347 115 310Q155 273 183 252Q211 231 237 215.5Q263 200 282 191Q301 182 314.5 177Q328 172 343.5 167.5Q359 163 370 161Q381 159 391.5 158Q402 157 416.5 157.5Q431 158 444 160.5Q457 163 466.5 166.5Q476 170 490.5 179.5Q505 189 511 195Q517 201 522.5 209Q528 217 532.5 228Q537 239 538.5 246.5Q540 254 540 265.5Q540 277 538 287.5Q536 298 530 312.5Q524 327 517 337.5Q510 348 509.5 354.5Q509 361 511 365.5Q513 370 517.5 373.5Q522 377 528.5 378Q535 379 541 376.5Q547 374 554 365.5Q561 357 567.5 345Q574 333 578.5 320.5Q583 308 585.5 295.5Q588 283 588.5 268.5Q589 254 588 245.5Q587 237 584 225.5Q581 214 576 202.5Q571 191 562 178Q553 165 544.5 156.5Q536 148 526.5 141Q517 134 509.5 130Q502 126 488.5 121Q475 116 464 113.5Q453 111 439.5 109.5Q426 108 413.5 108Z',
    color: '#FF1644',
  },
  {
    d: 'M414 6Q388 6 366.5 9Q345 12 323 17.5Q301 23 276 32.5Q251 42 223 56.5Q195 71 171 86.5Q147 102 126 118.5Q105 135 103 138.5Q101 142 100.5 148.5Q100 155 102 159.5Q104 164 108 167.5Q112 171 117 172.5Q122 174 129 172.5Q136 171 154.5 157Q173 143 187.5 133.5Q202 124 234 107Q266 90 294 79.5Q322 69 341 64.5Q360 60 379.5 57.5Q399 55 414 55Q429 55 446.5 57Q464 59 483 64.5Q502 70 521 79.5Q540 89 551 97Q562 105 573.5 116.5Q585 128 589.5 134Q594 140 605 159.5Q616 179 621.5 198.5Q627 218 628 238Q629 258 625.5 278Q622 298 617 312Q612 326 598 351Q584 376 584.5 383.5Q585 391 589 396Q593 401 598 403Q603 405 607.5 405Q612 405 616.5 403Q621 401 624.5 397.5Q628 394 635 383.5Q642 373 648.5 360.5Q655 348 663.5 322.5Q672 297 674.5 280Q677 263 676.5 240.5Q676 218 672 198.5Q668 179 659.5 158Q651 137 640 119.5Q629 102 615 87Q601 72 588.5 62Q576 52 562.5 44Q549 36 535.5 30Q522 24 496.5 17Q471 10 455.5 8Q440 6 414 6Z',
    color: '#FF0B41',
  },
] as const;

/**
 * The Crista as a loader (proposal D11): the living figure of Bodyprint+ with no reading, so one
 * colour and no verdict. 2 lines in a control, 3 in a highlight, the 4 rings from 40 px up.
 * Each line is the Crista's organic ring at rest, drawn in a 48-unit box; the spinner draws them
 * with CSS (`cristaLoaderDraw`), so the wait keeps moving while the page is busy.
 */
const CRISTA_LOADER_RADII: Record<2 | 3 | 4, readonly number[]> = {
  2: [10, 20],
  3: [7, 13.5, 20],
  4: [5.5, 10.5, 15.5, 20.5],
};
/** The loader's loop (PF20): the lines draw from the core outward for 70% of it, then fade together. */
export const CRISTA_LOADER_CYCLE_MS = 1800;
/** Reduced motion: the drawn Crista only blinks, slowly (PF20). */
export const CRISTA_LOADER_BLINK_MS = 2400;

const round2 = (value: number) => Math.round(value * 100) / 100;

/**
 * When line `drawIndex` (0 = the core) draws itself, in % of the loop: each line starts 7% of the
 * drawing after the one inside it and takes 51% of it; the drawing is the first 70% of the loop.
 * The same timing as the app's `cristaLoaderFrame`.
 */
export function getCristaLoaderDrawWindow(drawIndex: number) {
  return {
    start: round2(70 * drawIndex * 0.07),
    end: round2(Math.min(70, 70 * (drawIndex * 0.07 + 0.51))),
  };
}

/**
 * The neutral "Imprimir" of the Crista (CR04, PF20) for line `drawIndex`: hidden, then drawn along
 * its path (`pathLength` 100), held, and faded with the rest in the last 30% of the loop.
 */
export function cristaLoaderDraw(drawIndex: number) {
  const { start, end } = getCristaLoaderDrawWindow(drawIndex);
  return keyframes`
    0%, ${start}% { stroke-dashoffset: 100; opacity: 0; }
    ${round2(start + 0.01)}% { stroke-dashoffset: 100; opacity: 1; }
    ${end}%, 70% { stroke-dashoffset: 0; opacity: 1; }
    100% { stroke-dashoffset: 0; opacity: 0; }
  `;
}

/** One draw per line, core first: the loader shows at most the 4 rings. */
export const CRISTA_LOADER_DRAWS = [0, 1, 2, 3].map(cristaLoaderDraw);

/** Reduced motion: the drawn Crista blinks slowly between full and 40%, never moves. */
export const cristaLoaderBlink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

export interface CristaLoaderRing {
  d: string;
  /** Approximate path length in the 48 box, for the visible arc. */
  length: number;
  /** The arc drawn of each ring: long outside, short in the core. */
  dash: number;
  opacity: number;
}

export function getCristaLoaderRings(count: 2 | 3 | 4, seed = 1.7): CristaLoaderRing[] {
  const radii = CRISTA_LOADER_RADII[count];
  return radii
    .map((radius, index) => {
      let d = '';
      let length = 0;
      let prev: [number, number] | null = null;
      for (let k = 0; k <= 64; k += 1) {
        const a = (k / 64) * Math.PI * 2 - Math.PI / 2;
        const r =
          radius *
            (1 +
              0.04 * Math.sin(2 * a + seed) +
              0.028 * Math.sin(3 * a + seed * 2.3) +
              0.015 * Math.sin(5 * a + seed * 0.7)) +
          0.12 * (0.6 * Math.sin(4 * a + index * 1.1) + 0.4 * Math.sin(6 * a + seed));
        const point: [number, number] = [24 + r * Math.cos(a), 24 + r * Math.sin(a)];
        if (prev) length += Math.hypot(point[0] - prev[0], point[1] - prev[1]);
        prev = point;
        d += `${k === 0 ? 'M' : 'L'}${point[0].toFixed(2)} ${point[1].toFixed(2)}`;
      }
      const share = 0.34 + 0.34 * (index / Math.max(1, radii.length - 1));
      return {
        d: `${d}Z`,
        length,
        dash: length * share,
        opacity: 0.45 + 0.55 * ((index + 1) / radii.length),
      };
    })
    .reverse();
}

/**
 * How many rings the spinner draws: from 40 px up the Crista shows all four; below that `triple`
 * draws three and `double` two.
 */
export function getCristaLoaderRingCount(size: number, variant: CristaSpinnerVariant): 2 | 3 | 4 {
  if (size >= 40) return 4;
  return variant === 'triple' ? 3 : 2;
}

/** The rings' ink: on a Dark Blue ground the default ink turns paper; a chosen colour stays. */
export function getCristaLoaderInk(color: string, onDark: boolean) {
  return onDark && color === controlTokens.primary ? surfaceTokens.paper : color;
}

/** Stroke in the 48 box: about 2.4 px on screen at any size, a little heavier when tiny. */
export function getCristaLoaderStroke(size: number) {
  return ((size <= 16 ? 2.6 : 2.3) * 48) / size;
}
